import React, { useEffect, useState, useCallback } from 'react';
import { Pencil, Trash2, AlertCircle, X, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import OperatingInstructionHeader from './operatingInstructionHeader';
import UploadDocumentModal from './uploadDocumentModal';
import AddOperatingInstruction from './addOperatingInstructionModal';
import SuccessModal from '../../../components/ConfirmModal/SuccessModal';
import { deleteOperationInstructionByID, getOperationInstruction } from '../../../services/operationInstructionServices';
import ConfirmModal from '../../../components/ConfirmModal/ConfirmModal';
import Loader from '../../../components/Loader/Loader';
import Pagination from '../../../components/Pagination/Pagination';

type DocumentRow = {
  id: number;
  reference_number: string;
  title: string;
  site: string | number | null;
  instruction_date: string;
  status: string | null;
};

type UploadedFileCard = {
  name: string;
  size: number;
  dataUrl: string;
};

const statusColors: Record<string, string> = {
  New: 'text-purple-600',
  Authorized: 'text-orange-600',
  Received: 'text-green-600',
};

const ITEMS_PER_PAGE = 10;
const UPLOADED_FILES_STORAGE_KEY = 'operatingInstruction_uploadedFiles';

const OperatingInstructions: React.FC = () => {
  const navigate = useNavigate();

  // Modals
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [openAddInstruction, setOpenAddInstruction] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<'success' | 'error'>('success');

  // Data
  const [allDocuments, setAllDocuments] = useState<DocumentRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Persist uploaded file cards across refreshes
  // const [uploadedFileCards, setUploadedFileCards] = useState<UploadedFileCard[]>(() => {
  //   try {
  //     const stored = localStorage.getItem(UPLOADED_FILES_STORAGE_KEY);
  //     return stored ? (JSON.parse(stored) as UploadedFileCard[]) : [];
  //   } catch {
  //     return [];
  //   }
  // });

  // Persist uploadedFileCards to localStorage whenever it changes
  // useEffect(() => {
  //   try {
  //     localStorage.setItem(UPLOADED_FILES_STORAGE_KEY, JSON.stringify(uploadedFileCards));
  //   } catch {
  //     // silently ignore storage errors
  //   }
  // }, [uploadedFileCards]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(allDocuments.length / ITEMS_PER_PAGE));
  const documents = allDocuments.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const [searchQuery, setSearchQuery] = useState('');

  // Delete flow
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Editing
  const [editData, setEditData] = useState<any | null>(null);

  // Modal message and title
  const [modalTitle, setModalTitle] = useState<string>('Success');
  const [modalMessage, setModalMessage] = useState<string>('Operation successful.');

  // Error notification
  const [apiError, setApiError] = useState('');
  const [showError, setShowError] = useState(false);

  const fetchData = useCallback(async (search = '') => {
    setLoading(true);

    try {
      // pass search query to API
      const res = await getOperationInstruction(1, search);

      // support different shapes: res, res.data, res.data.data
      const payload = (res && (res.data?.data || res.data || res)) || [];
      const items = Array.isArray(payload) ? payload : [];

      // IMPORTANT: update table state
      setAllDocuments(items);

      setCurrentPage(1);

      setError(null);
    } catch (err: any) {
      console.error('Failed to load operating instructions', err);
      const message =
        err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Failed to load operating instructions.';
      setApiError(message);
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
      setError('Failed to load operating instructions.');
      setAllDocuments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(searchQuery);
  }, [fetchData, searchQuery]);

  // CHANGED: stopPropagation prevents router navigation, window.open opens file directly in new tab
  const handleOpenFile = (file: UploadedFileCard, e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(file.dataUrl, '_blank');
  };

  const truncateTitle = (title: string, maxLength = 20) => {
    if (!title) return '';

    return title.length > maxLength ? title.substring(0, maxLength) + '...' : title;
  };

  return (
    <div className="p-6">
      <OperatingInstructionHeader
        onOpenUploadModal={() => setIsUploadModalOpen(true)}
        onOpenAddInstruction={() => {
          setEditData(null);
          setOpenAddInstruction(true);
        }}
        onSearch={(query) => setSearchQuery(query)}
      />

      {/* <UploadDocumentModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onSuccess={(uploadedFiles) => {
          // Add new file cards (with dataUrl) to the display row
          setUploadedFileCards((prev) => [...prev, ...uploadedFiles]);
          // Show success modal
          setModalType('success');
          setModalTitle('Success');
          setModalMessage('Document uploaded successfully.');
          setShowModal(true);
          fetchData();
        }}
      /> */}

      <AddOperatingInstruction
        isOpen={openAddInstruction}
        onClose={() => {
          setOpenAddInstruction(false);
          setEditData(null);
        }}
        onConfirm={async (returnedItem: any, type: 'create' | 'update') => {
          // Show success and refresh table
          setModalType('success');
          setModalTitle('Success');
          setModalMessage(
            type === 'create' ? 'Operating instruction created successfully.' : 'Operating instruction updated successfully.',
          );
          setShowModal(true);
          await fetchData();
          setEditData(null);
        }}
        onError={(errorMsg: string) => {
          // Show error notification
          setApiError(errorMsg);
          setShowError(true);
          setTimeout(() => setShowError(false), 5000);
        }}
        editData={editData}
      />

      <SuccessModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={modalTitle}
        message={modalMessage}
        buttonText="" // No button needed for simple notifications
        type={modalType}
        autoClose={5000}
      />

      {loading && <Loader />}
      {error && <p className="text-center text-red-500 my-4">{error}</p>}

      {!loading && !error && (
        <>
          {/* Uploaded file cards row — matches Figma design, clickable to open file */}
          {/* {uploadedFileCards.length > 0 && (
            <div className="flex gap-4 mb-6 flex-wrap">
              {uploadedFileCards.map((file, idx) => (
                <div
                  key={idx}
                  onClick={(e) => handleOpenFile(file, e)} // CHANGED: pass event for stopPropagation
                  className="flex items-center gap-3 border border-gray-200 rounded-xl px-4 py-3 bg-white shadow-sm min-w-[220px] max-w-[260px] cursor-pointer hover:shadow-md hover:border-blue-300 transition-all"
                >
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                    <FileText className="text-blue-500" size={22} />
                  </div>
                  <div className="overflow-hidden">
                    <p className="text-sm font-medium text-gray-800 truncate" title={file.name}>
                      {file.name.length > 28 ? file.name.slice(0, 25) + '..' : file.name}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">{(file.size / 1024).toFixed(0)}kb</p>
                  </div>
                </div>
              ))}
            </div>
          )} */}

          <table className="min-w-full text-sm text-left border rounded shadow">
            <thead className="text-gray-500 text-xs">
              <tr className=" text-left h-[45px] ">
                <th className="p-3 border text-xs font-medium text-gray-500">Sl. No</th>
                <th className="p-3 border text-xs font-medium text-gray-500">Ref No.</th>
                <th className="p-3 border text-xs font-medium text-gray-500">Created Date</th>
                <th className="p-3 border text-xs font-medium text-gray-500">Title</th>
                <th className="p-3 border text-xs font-medium text-gray-500">Site</th>
                <th className="p-3 border text-xs font-medium text-gray-500">Status</th>
                <th className="p-3 border text-xs font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {documents.map((doc, idx) => (
                <tr
                  key={doc.id}
                  className="border-b hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/OperatingInstruction/${doc.id}`)}
                >
                  <td className="p-3 text-gray-500 border text-center">{(currentPage - 1) * ITEMS_PER_PAGE + idx + 1}</td>
                  <td className="p-3 border font-medium">{doc.reference_number || '—'}</td>
                  <td className="p-3 border font-medium">
                    {doc.instruction_date ? new Date(doc.instruction_date).toLocaleDateString('en-GB') : '—'}
                  </td>
                  <td className="p-3 border font-medium max-w-[250px]" title={doc.title}>
                    <div className="max-w-[250px] truncate">{doc.title || '—'}</div>
                  </td>
                  <td className="p-3 border font-medium">{doc.site ?? '—'}</td>
                  <td className={`p-3 font-medium ${statusColors[doc.status || ''] ?? ''} border`}>
                    {doc.status ? `● ${doc.status}` : '—'}
                  </td>
                  <td className="p-3 flex gap-2 border">
                    {doc.status === 'New' ? (
                      <>
                        <button
                          className="text-red-500"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteId(doc.id);
                            setIsDeleteOpen(true);
                          }}
                          aria-label={`Delete instruction ${doc.id}`}
                        >
                          <Trash2 size={16} />
                        </button>
                        <button
                          className="text-blue-500"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditData(doc);
                            setOpenAddInstruction(true);
                          }}
                          aria-label={`Edit instruction ${doc.id}`}
                        >
                          <Pencil size={16} />
                        </button>
                      </>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />

          <ConfirmModal
            isOpen={isDeleteOpen}
            title="Are you sure you want to delete this instruction?"
            message=""
            confirmText="Yes, Delete"
            loadingText="Deleting.."
            isLoading={isDeleting}
            cancelText="Cancel"
            onCancel={() => {
              setIsDeleteOpen(false);
              setDeleteId(null);
            }}
            onConfirm={async () => {
              if (deleteId === null || isDeleting) return;
              setIsDeleting(true);

              try {
                await deleteOperationInstructionByID(deleteId);
                setModalType('success');
                setModalTitle('Success');
                setModalMessage('Operating instruction deleted successfully.');
                setShowModal(true);
                await fetchData();
              } catch (error: any) {
                console.error('Error deleting instruction:', error);

                // Extract error message
                let errorMsg = 'Failed to delete operation instruction.';
                if (error?.response?.data?.message) {
                  errorMsg = error.response.data.message;
                } else if (error?.response?.data?.error) {
                  errorMsg = error.response.data.error;
                } else if (error?.message) {
                  errorMsg = error.message;
                }

                setApiError(errorMsg);
                setShowError(true);
                setTimeout(() => setShowError(false), 5000);
              } finally {
                setIsDeleting(false);
                setIsDeleteOpen(false);
                setDeleteId(null);
              }
            }}
          />
        </>
      )}

      {/* Error Notification */}
      {showError && (
        <div className="fixed bottom-6 right-6 z-[100]">
          <div className="bg-red-500 text-white rounded-lg shadow-lg p-4 pr-12 min-w-[300px] max-w-[500px] relative animate-slide-in">
            <button onClick={() => setShowError(false)} className="absolute top-3 right-3 text-white hover:text-gray-200 transition-colors">
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold mb-1">Error</h4>
                <p className="text-sm">{apiError}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OperatingInstructions;
